import type { TFunction } from 'i18next';
import { API_BASE_URL } from '../constants';
import { altchaWidgetHtml, argon2idRegistrationScript } from './altcha';
import { generateHtmlFromSpecV2 } from './form-spec-to-html';
import type { FormField, FormSpec } from './form-specs';
import { apiOrigin, frontendOrigin } from './origins';

export type SiteMethod = 'own' | 'hosted' | 'link';

export const HOSTED_FRAMEWORKS = ['bulma', 'bootstrap', 'formshive'] as const;
export type HostedFramework = (typeof HOSTED_FRAMEWORKS)[number];

export function submissionUrl(formId: string): string {
  return `${API_BASE_URL}/digest/${formId}`;
}

export function challengeUrl(formId: string): string {
  return `${API_BASE_URL}/forms/${formId}/challenge/altcha/v3`;
}

/** The `<head>` script of the captcha, as the old Manual tab printed it. */
export function captchaScript(): string {
  return argon2idRegistrationScript();
}

export function captchaWidget(formId: string, indent = ''): string {
  return altchaWidgetHtml(challengeUrl(formId), indent);
}

export function hasFields(spec: FormSpec | null): spec is FormSpec {
  return spec !== null && Object.keys(spec.fields).length > 0;
}

export function hasFileField(spec: FormSpec | null): boolean {
  return hasFields(spec) && Object.values(spec.fields).some((f) => f.field === 'file');
}

/** Step 2 of the own-form instructions: the real fields when there are some, else a skeleton. */
export function ownFormExample(
  formId: string,
  captcha: boolean,
  spec: FormSpec | null,
  t: TFunction
): string {
  const url = submissionUrl(formId);
  if (hasFields(spec)) {
    return generateHtmlFromSpecV2(spec, url, captcha, challengeUrl(formId), {
      captchaNote: t('form.site.prompt.captchaNote'),
      submit: t('form.site.prompt.send'),
    });
  }
  return [
    `<form action="${url}" method="POST">`,
    `  <!-- ${t('form.site.prompt.keepInputs')} -->`,
    ...(captcha ? [captchaWidget(formId, '  ')] : []),
    `  <button type="submit">${t('form.site.prompt.send')}</button>`,
    '</form>',
  ].join('\n');
}

export function hostedFormUrl(formId: string, framework: HostedFramework): string {
  return `${API_BASE_URL}/forms/${formId}/html?iframe=true&css_framework=${framework}&css_embed=true&redirect=html`;
}

export function iframeSnippet(formId: string, framework: HostedFramework, title: string): string {
  const safeTitle = title.replace(/"/g, '&quot;');
  return `<iframe src="${hostedFormUrl(formId, framework)}" title="${safeTitle}" frameborder="0" width="100%" height="600"></iframe>`;
}

// The embed widget and link.html post here when no `api_url` is given.
const DEFAULT_API_BASE_URL = 'https://api.formshive.com/v1';

/** `api_url` only when this build talks to another API than the widget's default. */
function customApiBase(): string | null {
  return API_BASE_URL && API_BASE_URL !== DEFAULT_API_BASE_URL ? API_BASE_URL : null;
}

export function linkUrl(formId: string, framework: HostedFramework, title: string): string {
  const api = customApiBase();
  return `${frontendOrigin()}/link.html?form_id=${formId}&framework=${framework}&title=${encodeURIComponent(title)}${api ? `&api_url=${encodeURIComponent(api)}` : ''}`;
}

/** The embed widget reads `form_id` (and `api_url`) and ships as `embed.min.js`. */
export function embedSnippet(formId: string, framework: HostedFramework, t: TFunction): string {
  const origin = frontendOrigin();
  const api = customApiBase();
  const apiAttr = api ? ` api_url="${api}"` : '';
  return `<div id="formshive" form_id="${formId}" framework="${framework}"${apiAttr}><a href="${origin}/link.html?form_id=${formId}&framework=${framework}">${t('form.site.prompt.embedLink')}</a></div><script type="text/javascript" async src="${origin}/embed.min.js"></script>`;
}

function describeField(key: string, field: FormField, t: TFunction): string {
  const type = t(`form.fieldType.${field.is_email ? 'email' : field.field}`, {
    defaultValue: field.field,
  });
  const parts = [type, t(field.required ? 'form.fields.required' : 'form.fields.optional')];
  const options = field.options ?? field.is_in;
  if (options) parts.push(t('form.site.prompt.oneOf', { options }));
  if (field.hidden && field.is_empty) parts.push(t('form.site.prompt.trap'));
  const label =
    field.label && field.label !== key
      ? ` ${t('form.site.prompt.labelled', { label: field.label })}`
      : '';
  return `   - ${key}${label} (${parts.join(', ')})`;
}

const indent = (text: string, prefix: string) =>
  text
    .split('\n')
    .map((line) => `${prefix}${line}`)
    .join('\n');

export interface SetupPromptInput {
  formId: string;
  title: string;
  method: SiteMethod;
  captcha: boolean;
  spec: FormSpec | null;
  redirectUrl?: string | null;
  allowedOrigins: string[];
  firstRecipient?: string | null;
  framework: HostedFramework;
}

/** The AI setup prompt in the UI language: built only from the form's real settings, never invented fields. */
export function buildSetupPrompt(input: SetupPromptInput, t: TFunction): string {
  const url = submissionUrl(input.formId);
  const title = input.title;
  const lines: string[] = [];
  const steps: string[] = [];

  if (input.method === 'own') {
    lines.push(t('form.site.prompt.ownIntro', { title }));
    steps.push(t('form.site.prompt.ownAction', { url }));
    if (input.captcha) {
      steps.push(
        [
          t('form.site.prompt.captchaHead'),
          indent(captchaScript(), '   '),
          `   ${t('form.site.prompt.captchaForm')}`,
          indent(captchaWidget(input.formId), '   '),
          `   ${t('form.site.prompt.captchaRequired')}`,
        ].join('\n')
      );
    }
    if (hasFields(input.spec)) {
      const fieldLines = Object.entries(input.spec.fields).map(([key, field]) =>
        describeField(key, field, t)
      );
      const notes: string[] = [];
      if (input.spec.settings?.discard_additional_fields) {
        notes.push(`   ${t('form.site.prompt.discardNote')}`);
      }
      if (hasFileField(input.spec)) {
        notes.push(`   ${t('form.site.prompt.fileNote')}`);
      }
      steps.push([t('form.site.prompt.fieldsIntro'), ...fieldLines, ...notes].join('\n'));
    } else {
      steps.push(t('form.site.prompt.noFields'));
    }
  } else if (input.method === 'hosted') {
    lines.push(t('form.site.prompt.hostedIntro', { title }));
    steps.push(
      [
        t('form.site.prompt.hostedPaste'),
        indent(iframeSnippet(input.formId, input.framework, title), '   '),
      ].join('\n')
    );
    steps.push(
      t(hasFields(input.spec) ? 'form.site.prompt.hostedRendered' : 'form.site.prompt.hostedEmpty')
    );
  } else {
    lines.push(t('form.site.prompt.linkIntro', { title }));
    steps.push(
      t('form.site.prompt.linkTo', { url: linkUrl(input.formId, input.framework, title) })
    );
    if (!hasFields(input.spec)) {
      steps.push(t('form.site.prompt.linkEmpty'));
    }
  }

  steps.push(
    input.redirectUrl
      ? t('form.site.prompt.redirect', { url: input.redirectUrl })
      : t('form.site.prompt.noRedirect')
  );

  if (input.allowedOrigins.length > 0) {
    const origins = input.allowedOrigins.join(', ');
    steps.push(
      input.method === 'own'
        ? t('form.site.prompt.origins', { origins })
        : t('form.site.prompt.originsHosted', {
            origins,
            formshive: input.method === 'hosted' ? apiOrigin() : frontendOrigin(),
          })
    );
  }

  steps.push(
    input.firstRecipient
      ? t('form.site.prompt.checkRecipient', { email: input.firstRecipient })
      : t('form.site.prompt.checkMessages')
  );

  lines.push('');
  steps.forEach((step, i) => {
    lines.push(`${i + 1}. ${step}`);
  });
  return lines.join('\n');
}

export function curlExample(formId: string, spec: FormSpec | null, origin?: string): string {
  const data = hasFields(spec)
    ? testSubmissionData(spec, 'test@example.com')
    : { name: 'Test', email: 'test@example.com', message: 'Hello' };
  return [
    `curl -X POST "${submissionUrl(formId)}?redirect=none" \\`,
    ...(origin ? [`  -H "Origin: ${origin}" \\`] : []),
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(data).replace(/'/g, "'\\''")}'`,
  ].join('\n');
}

function matchesPattern(pattern: string | undefined, value: string): boolean {
  if (!pattern) return true;
  try {
    return new RegExp(pattern).test(value);
  } catch {
    return false;
  }
}

function textValue(field: FormField, fallback: string): string {
  if (field.placeholder && matchesPattern(field.is_pattern, field.placeholder)) {
    return field.placeholder;
  }
  let value = fallback;
  if (typeof field.is_min === 'number' && value.length < field.is_min) {
    value = value.padEnd(field.is_min, '.');
  }
  if (typeof field.is_max === 'number' && field.is_max > 0 && value.length > field.is_max) {
    value = value.slice(0, field.is_max);
  }
  return value;
}

function firstOption(field: FormField): string | undefined {
  const list = field.is_in ?? field.options;
  return list
    ?.split(',')
    .map((v) => v.trim())
    .find(Boolean);
}

/** Values that pass the form's own field rules, for the test submission and the curl example. */
export function testSubmissionData(
  spec: FormSpec | null,
  email: string
): Record<string, string | number | string[]> {
  if (!hasFields(spec)) {
    return {
      name: 'Formshive test',
      email,
      message: 'This is a test submission sent from the form page in Formshive.',
    };
  }
  const data: Record<string, string | number | string[]> = {};
  for (const [key, field] of Object.entries(spec.fields)) {
    if (field.is_empty || field.field === 'file' || field.field === 'submit') continue;
    const option = firstOption(field);
    if (field.is_email || field.field === 'email') {
      data[key] = email;
    } else if (field.field === 'number') {
      data[key] = typeof field.is_min === 'number' ? field.is_min : 1;
    } else if (field.field === 'date') {
      data[key] = new Date().toISOString().slice(0, 10);
    } else if (field.field === 'datetime') {
      data[key] = new Date().toISOString().slice(0, 16);
    } else if (field.field === 'time') {
      data[key] = '12:00';
    } else if (field.field === 'url' || field.is_url) {
      data[key] = 'https://example.com';
    } else if (field.field === 'tel') {
      data[key] = textValue(field, '+10000000000');
    } else if (option !== undefined) {
      data[key] = field.multiple ? [option] : option;
    } else if (field.field === 'checkbox') {
      data[key] = 'on';
    } else if (field.field === 'hidden') {
      if (field.value !== undefined && field.value !== null) data[key] = String(field.value);
    } else {
      data[key] = textValue(field, 'Formshive test');
    }
  }
  return data;
}

export function requiredFileField(spec: FormSpec | null): string | null {
  if (!hasFields(spec)) return null;
  const entry = Object.entries(spec.fields).find(([, f]) => f.field === 'file' && f.required);
  return entry ? entry[0] : null;
}
